// Field-value distribution drawer. Side panel with top-50 unique
// values for a column + their counts + a horizontal bar showing
// relative share + a per-row "filter to this" button. Two cross-
// links keep it composable: '📊 Open as pivot' switches to full-
// table groupBy mode; '× close' returns to the data surface.
// Operator's mental model: 'I clicked a column to peek at what's in
// there'. Extracted from DataSurfaceV3 during the agent-velocity
// refactor — all state stays in the parent.

import * as React from 'react';
import type { FilterRow } from '../v2/recipe-sql';

type ToastApi = {
  show: (
    msg: React.ReactNode,
    opts?: { kind?: 'success' | 'info' | 'error' | 'warning'; ttlMs?: number | null },
  ) => void;
};

export function DistributionDrawer({
  field,
  loading,
  rows,
  durationMs,
  setField,
  setGroupByField,
  setFilters,
  toast,
}: {
  field: string | null;
  loading: boolean;
  rows: Array<{ value: string; count: number }> | null;
  durationMs: number | null;
  setField: (v: string | null) => void;
  setGroupByField: (v: string | null) => void;
  setFilters: React.Dispatch<React.SetStateAction<FilterRow[]>>;
  toast: ToastApi;
}): React.ReactElement | null {
  if (!field) return null;
  return (
    <div
      onClick={() => setField(null)}
      style={{
        position: 'fixed', inset: 0,
        background: 'oklch(0.18 0.01 250 / 0.35)',
        display: 'flex', justifyContent: 'flex-end',
        zIndex: 1400,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 'min(480px, 100vw)', height: '100vh',
          background: 'var(--bg)', borderLeft: '1px solid var(--line)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 32px oklch(0.18 0.01 250 / 0.18)',
        }}
      >
        <div style={{
          padding: '20px 24px 14px', borderBottom: '1px solid var(--line)',
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8,
        }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 4 }}>
              Distribution
            </div>
            <strong style={{ fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--ink)' }}>{field}</strong>
          </div>
          <button
            type="button"
            onClick={() => setField(null)}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', color: 'var(--ink-3)', cursor: 'pointer', fontSize: 18, padding: 0, lineHeight: 1 }}
          >×</button>
        </div>
        <div style={{
          padding: '10px 24px',
          display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
          borderBottom: '1px solid var(--line)',
          fontSize: 11, color: 'var(--ink-3)',
        }}>
          <span>
            {loading ? 'querying…' : rows ? `top ${rows.length} of ${rows.length === 50 ? '50+' : rows.length} unique values` : ''}
            {durationMs !== null && !loading && (
              <span
                style={{ marginLeft: 8, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}
                title="BigQuery round-trip including 400ms client debounce"
              >⏱ {durationMs < 1000 ? `${durationMs}ms` : `${(durationMs / 1000).toFixed(2)}s`}</span>
            )}
          </span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() => {
              setGroupByField(field);
              setField(null);
            }}
            className="btn sm ghost"
            style={{ padding: '3px 10px', fontSize: 11 }}
            title="Replaces the main table with a Group-by pivot on this column. Operator can drill back to row view from any value."
          >📊 Pivot table by this →</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {loading && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--ink-3)' }}>
              Computing distribution…
            </div>
          )}
          {rows && rows.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--ink-3)' }}>
              No events have <code style={{ fontFamily: 'var(--mono)' }}>{field}</code> set.
            </div>
          )}
          {rows && rows.length > 0 && (() => {
            const totalCount = rows.reduce((s, r) => s + r.count, 0);
            const maxCount = Math.max(...rows.map(r => r.count));
            return (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {rows.map((row, i) => {
                    const pct = totalCount > 0 ? (row.count / totalCount) * 100 : 0;
                    const barWidth = maxCount > 0 ? (row.count / maxCount) * 100 : 0;
                    return (
                      <tr
                        key={i}
                        onClick={() => {
                          setFilters(prev => {
                            const idx = prev.findIndex(f => f.field_path === field && f.op === 'eq');
                            if (idx >= 0) return prev.map((f, j) => j === idx ? { ...f, value: row.value } : f);
                            return [...prev, { field_path: field, op: 'eq', value: row.value }];
                          });
                          setField(null);
                          toast.show(`Filtered to ${field} = ${row.value.length > 40 ? row.value.slice(0, 40) + '…' : row.value}`, { kind: 'success' });
                        }}
                        title={`Click to filter where ${field} = ${row.value}`}
                        style={{
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--line)',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-sunken)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <td style={{
                          position: 'relative',
                          padding: '8px 12px 8px 24px',
                          fontFamily: 'var(--mono)', fontSize: 12,
                          color: 'var(--ink)',
                          maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          <div style={{
                            position: 'absolute', left: 24, right: 12, top: 4, bottom: 4,
                            background: 'oklch(0.92 0.10 145 / 0.35)',
                            width: `calc((100% - 36px) * ${barWidth / 100})`,
                            borderRadius: 2,
                            pointerEvents: 'none',
                          }} />
                          <span style={{ position: 'relative' }}>{row.value || '(empty)'}</span>
                        </td>
                        <td style={{
                          padding: '8px 24px 8px 8px', textAlign: 'right',
                          fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-2)',
                          fontVariantNumeric: 'tabular-nums',
                          whiteSpace: 'nowrap',
                        }}>
                          {row.count.toLocaleString()} <span style={{ color: 'var(--ink-3)', fontSize: 10.5 }}>· {pct.toFixed(1)}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
        </div>
        <div style={{
          padding: '10px 24px',
          borderTop: '1px solid var(--line)',
          fontSize: 10.5, color: 'var(--ink-3)', fontStyle: 'italic',
        }}>
          Click a value to add it as a filter on the main table.
        </div>
      </div>
    </div>
  );
}
